import { IMPORT_FORMAT, IMPORT_NOTE_KEY, IMPORT_PARTS, IMPORT_VERSION, type ImportDocument, type ImportPart, type ImportSchedule, type ImportTask, schemaLimits } from '@pst/shared';
import { exchangeApi, projectDataApi, scheduleApi, type Project, type WbsNode } from '../api';

/**
 * Turning a project back into a PST-Import document (docs/IMPORT.md), and the
 * few transformations built on that: duplicate, template, date shift.
 *
 * The export is exact rather than pretty: durations, pinned start dates and
 * relationships with their type, so that importing it again yields the same
 * network, not an approximation re-derived from dates.
 */

/** The parts a project can be exported with, given the active modules. */
export function availableParts(active: ReadonlySet<string>): ImportPart[] {
  return IMPORT_PARTS.filter((p) => active.has(p.module)).map((p) => p.key);
}

/** The parts a file actually carries - what an import can offer. */
export function partsIn(doc: ImportDocument): ImportPart[] {
  const p = doc.project;
  const hasDetails = Object.keys(p).some((k) => k !== 'number' && k !== 'name') || (doc.contacts?.length ?? 0) > 0;
  return IMPORT_PARTS.map((x) => x.key).filter((k) =>
    k === 'projectdata' ? hasDetails : k === 'schedule' ? !!doc.schedule : k === 'costs' ? !!doc.costs : k === 'tendering' ? !!doc.tendering : !!doc.time,
  );
}

export async function exportProject(
  project: Project,
  active: ReadonlySet<string>,
  parts: ReadonlySet<ImportPart> = new Set(availableParts(active)),
  options: { timeEntries?: boolean } = {},
): Promise<ImportDocument> {
  const doc: ImportDocument = {
    format: IMPORT_FORMAT,
    version: IMPORT_VERSION,
    project: { number: project.number, name: project.name },
  };
  const want = (part: ImportPart): boolean => parts.has(part) && active.has(IMPORT_PARTS.find((p) => p.key === part)!.module);

  if (want('costs')) doc.costs = await exchangeApi.costs(project.id);
  if (want('tendering')) doc.tendering = await exchangeApi.tendering(project.id);
  if (want('time')) doc.time = await exchangeApi.time(project.id, options.timeEntries ?? false);

  if (want('projectdata')) {
    const data = await projectDataApi.get(project.id);
    const d = data.details;
    Object.assign(doc.project, clean({
      shortName: d.shortName, reference: d.reference, description: d.description, client: d.client,
      offerDate: d.offerDate, offerNet: d.offerNet, offerGross: d.offerGross, bindingUntil: d.bindingUntil,
      hardDeadline: d.hardDeadline, planVersion: d.planVersion,
      facts: d.facts, assumptions: d.assumptions, risks: d.risks.map((r) => clean(r)), escalations: d.escalations,
      weeks: d.weeks.map((w) => clean(w)),
    }));
    doc.contacts = data.contacts.map(({ id: _id, sortOrder: _s, ...rest }) => { void _id; void _s; return clean(rest); });
  }

  if (want('schedule')) {
    const [plan, wbs, tasks, deps, summaries] = await Promise.all([
      scheduleApi.plan(project.id),
      scheduleApi.wbs(project.id),
      scheduleApi.tasks(project.id),
      scheduleApi.dependencies(project.id),
      scheduleApi.summaries(project.id),
    ]);
    // The file names a summary by its name; a name used twice gets a number, so two stay two.
    const taken = new Map<string, number>();
    const summaryName = new Map(summaries.map((s) => {
      const n = (taken.get(s.name) ?? 0) + 1;
      taken.set(s.name, n);
      return [s.id, n === 1 ? s.name : `${s.name} (${n})`.slice(0, 200)];
    }));
    const code = new Map(tasks.map((t) => [t.id, t.code]));
    const groupCode = new Map(wbs.map((w) => [w.id, w.code]));
    const schedule: ImportSchedule = clean({
      name: plan.name,
      bundesland: plan.calendar.bundesland,
      startDate: plan.startDate,
      targetFinish: plan.targetFinish,
    });
    schedule.groups = orderedGroups(wbs).map((w) => clean({
      code: w.code, name: w.name, parent: w.parentId ? groupCode.get(w.parentId) : undefined, color: w.color,
    }));
    schedule.tasks = [...tasks]
      .sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code, 'de', { numeric: true }))
      .map((t) => {
        const predecessors = deps
          .filter((d) => d.successorId === t.id)
          .map((d) => ({ code: code.get(d.predecessorId) ?? '', type: d.type, lagDays: d.lagDays }));
        let note = t.note;
        if (t.constraintType === 'finishNoEarlierThan' && t.constraintDate) {
          // Not expressible in the format; kept visible rather than lost.
          note = [note, `[Terminvorgabe „Ende nicht vor ${t.constraintDate}“ nicht übertragbar]`].filter(Boolean).join('\n');
        }
        const task: ImportTask = clean({
          code: t.code,
          name: t.name,
          group: t.wbsNodeId ? groupCode.get(t.wbsNodeId) : undefined,
          durationDays: t.durationDays,
          start: t.constraintType === 'startNoEarlierThan' ? t.constraintDate : undefined,
          owner: t.owner, position: t.position, deliverable: t.deliverable,
          status: t.status === 'open' ? undefined : t.status,
          deadline: t.deadline, note,
          summary: t.summaryId ? summaryName.get(t.summaryId) : undefined,
        });
        if (predecessors.length > 0) task.predecessors = predecessors;
        return task;
      });
    doc.schedule = schedule;
  }
  return doc;
}

/** Parents before children, siblings in their order - what the import needs. */
function orderedGroups(wbs: WbsNode[]): WbsNode[] {
  const children = new Map<string | null, WbsNode[]>();
  for (const node of wbs) {
    const list = children.get(node.parentId) ?? [];
    list.push(node);
    children.set(node.parentId, list);
  }
  const out: WbsNode[] = [];
  const walk = (parent: string | null): void => {
    for (const node of (children.get(parent) ?? []).sort((a, b) => a.sortOrder - b.sortOrder)) {
      out.push(node);
      walk(node.id);
    }
  };
  walk(null);
  return out;
}

/**
 * A template: the structure of a project without what belongs to one
 * particular job - no contacts, no client, no offer figures, all statuses
 * open, no costs or tendering, and time categories without budgets.
 */
export function toTemplate(doc: ImportDocument): ImportDocument {
  const p = doc.project;
  return {
    format: doc.format,
    version: doc.version,
    project: clean({
      number: 'VORLAGE',
      name: p.name,
      assumptions: p.assumptions,
      risks: p.risks,
      escalations: p.escalations,
      weeks: p.weeks,
    }),
    ...(doc.schedule
      ? {
          schedule: {
            ...doc.schedule,
            baseline: undefined,
            tasks: doc.schedule.tasks?.map(({ status: _s, ...task }) => { void _s; return task; }),
          },
        }
      : {}),
    ...(doc.time ? { time: { categories: doc.time.categories.map(({ code, label, unpaid }) => clean({ code, label, unpaid: unpaid || undefined })) } } : {}),
    // Cost structure, quantities, rates and lines carry over (A-30); the amounts
    // typed into the groups belong to one project and stay behind.
    ...(doc.costs ? { costs: { ...doc.costs, groups: doc.costs.groups?.map(({ amounts: _a, ...g }) => { void _a; return g; }) } } : {}),
    // Award units with their bill of quantities; bidders, prices, award and dates stay behind.
    ...(doc.tendering
      ? {
          tendering: {
            packages: doc.tendering.packages.map(({ bidders: _b, award: _w, sentOn: _s, deadline: _d, bindingUntil: _u, workStart: _ws, workEnd: _we, ...p }) => {
              void _b; void _w; void _s; void _d; void _u; void _ws; void _we;
              return { ...p, status: 'draft' as const };
            }),
          },
        }
      : {}),
  };
}

/**
 * A copy for a new project: everything but what is tied to real events -
 * no baseline, no bidders, prices or awards, no booked hours.
 */
export function toDuplicate(doc: ImportDocument, number: string, name: string): ImportDocument {
  return {
    ...doc,
    project: { ...doc.project, number, name },
    schedule: doc.schedule ? { ...doc.schedule, baseline: undefined } : undefined,
    tendering: doc.tendering
      ? { packages: doc.tendering.packages.map(({ bidders: _b, award: _a, ...p }) => { void _b; void _a; return { ...p, status: p.status === 'awarded' ? 'offers' : p.status }; }) }
      : undefined,
    time: doc.time ? { categories: doc.time.categories } : undefined,
  };
}

const DAY = 86_400_000;
function addDaysIso(iso: string | undefined, days: number): string | undefined {
  if (!iso) return iso;
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y!, m! - 1, d!) + days * DAY);
  return date.toISOString().slice(0, 10);
}

/**
 * Moves every date of the schedule (and the week grid) by whole weeks.
 * Whole weeks keep every date on its weekday, so rules like "on site only on
 * Thursday or Friday" still hold; holidays that now fall inside the plan are
 * reported by the import preview.
 */
export function shiftByWeeks(doc: ImportDocument, weeks: number): ImportDocument {
  if (weeks === 0) return doc;
  const days = weeks * 7;
  const shift = (iso: string | undefined): string | undefined => addDaysIso(iso, days);
  const s = doc.schedule;
  return {
    ...doc,
    project: {
      ...doc.project,
      ...(doc.project.hardDeadline ? { hardDeadline: shift(doc.project.hardDeadline) } : {}),
      ...(doc.project.weeks ? { weeks: doc.project.weeks.map((w) => ({ ...w, from: shift(w.from)!, to: shift(w.to)! , kw: undefined })) } : {}),
    },
    ...(s
      ? {
          schedule: clean({
            ...s,
            startDate: shift(s.startDate),
            targetFinish: shift(s.targetFinish),
            tasks: s.tasks?.map((t) => clean({ ...t, start: shift(t.start), end: shift(t.end), deadline: shift(t.deadline) })),
            milestones: s.milestones?.map((m) => clean({ ...m, date: shift(m.date), latest: shift(m.latest) })),
          }),
        }
      : {}),
  };
}

/** Earliest date of the schedule - what a shift is measured from. */
export function scheduleStart(doc: ImportDocument): string | undefined {
  const s = doc.schedule;
  if (!s) return undefined;
  const dates = [s.startDate, ...(s.tasks ?? []).map((t) => t.start), ...(s.milestones ?? []).map((m) => m.date)]
    .filter((d): d is string => !!d)
    .sort();
  return dates[0];
}

export function weeksBetween(fromIso: string, toIso: string): number {
  const from = Date.parse(`${fromIso}T00:00:00Z`);
  const to = Date.parse(`${toIso}T00:00:00Z`);
  return Math.round((to - from) / (7 * DAY));
}

/**
 * The notes an exported file carries for whoever edits it - mostly Claude
 * Chat: how the file is built, the rules that make an import fail, and the
 * limit of every field from the schema. The import ignores them.
 */
export function withNotes(doc: ImportDocument, schema: object | null): ImportDocument & { [IMPORT_NOTE_KEY]: object } {
  const { format, version, ...rest } = doc;
  return {
    format,
    version,
    [IMPORT_NOTE_KEY]: {
      zweck: 'PST-Import, Version 1: ein Projekt aus PST. Diese Hinweise schreibt PST beim Export mit; der Import ignoriert sie. Beim Bearbeiten (etwa in Claude Chat) stehen lassen oder weglassen.',
      regeln: [
        '"format" und "version" unverändert lassen; die Feldnamen bleiben englisch, keine neuen Felder erfinden.',
        'Jede Grenze unter "grenzen" einhalten – vor allem die Länge von Namen und Bezeichnungen. Lange Erklärungen gehören in "note" oder "description", nicht in "name".',
        'Datumsangaben als JJJJ-MM-TT. Zahlen ohne Tausenderpunkt, Dezimalpunkt statt Komma (in Formeln der Kostenplanung ist das Komma erlaubt).',
        'Nummern ("code") von Vorgängen und Meilensteinen sind eindeutig; "predecessors", "after" und "group" verweisen nur auf Nummern, die in der Datei stehen.',
        'Namen von Werten der Kostenplanung (costs.values[].name) nur aus Buchstaben, Ziffern und _, mit einem Buchstaben vorn – keine Umlaute, keine Leerzeichen.',
        'Kostengruppen als Zahl nach DIN 276, z. B. "330" oder "331.1".',
        'Was geändert werden soll, ändern; alles andere unverändert lassen. Nichts erfinden, was nicht in den Unterlagen steht.',
        'Die ganze Datei als gültiges JSON ausgeben, ohne Kommentare.',
      ],
      teile: {
        project: 'Projektdaten (number und name sind Pflicht)', contacts: 'Kontakte; "code" ist das Kürzel in "owner" der Vorgänge',
        schedule: 'Terminplan: groups = Phasen, tasks = Vorgänge mit Dauer in Arbeitstagen und Vorgängern (Typ EA, AA, EE, AE nach DIN 69900), milestones = Meilensteine',
        costs: 'Kostenplanung nach DIN 276: groups, values (Mengen und Kennwerte), items (Positionen mit Menge × Kennwert je Kostenstufe)',
        tendering: 'Ausschreibung und Vergabe: Vergabeeinheiten mit LV-Positionen, Bietern, Preisen', time: 'Zeiterfassung: Kategorien mit Stundenbudget',
      },
      schema: 'Das vollständige JSON-Schema liefert PST unter /api/docs/pst-import-v1.schema.json (auch über „Anleitung kopieren“ im Import).',
      ...(schema ? { grenzen: schemaLimits(schema) } : {}),
    },
    ...rest,
  };
}

export function downloadJson(doc: object, filename: string): void {
  const blob = new Blob([JSON.stringify(doc, null, 2) + '\n'], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Drops null, undefined and empty strings, so the file carries only what is there. */
type Cleaned<T> = { [K in keyof T]: Exclude<T[K], null> };
function clean<T extends object>(value: T): Cleaned<T> {
  return Object.fromEntries(
    Object.entries(value).filter(([, v]) => v !== null && v !== undefined && v !== ''),
  ) as Cleaned<T>;
}
